/**
 * CharactersList component - displays characters on a hex.
 * Shows character creation prompt if user has no primary character.
 * Shows recruitment option if user has Camp Hall with capacity.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Character, CharactersIndexResponse } from '@/types/characters';
import { CharacterSheet } from './CharacterSheet';
import { CreateCharacterModal } from './CreateCharacterModal';
import { RecruitCharacterModal } from './RecruitCharacterModal';

interface RecruitmentCapacity {
    total: number;
    base: number;
    from_level: number;
    from_assigned: number;
    camp_hall_level: number;
    camp_hall_stars: number;
    assigned_count: number;
    has_camp_hall: boolean;
}

interface RecruitmentInfo {
    capacity: RecruitmentCapacity;
    current_count: number;
    available_slots: number;
    can_recruit: boolean;
    xp_bonus: number;
    reason: string | null;
}

interface CharactersListProps {
    /** The hex ID to show characters for */
    hexId: number;
    /** Whether the user is the hex owner */
    isOwner: boolean;
    /** Whether the user has claimed at least one hex (required for character creation) */
    hasClaimedHex: boolean;
    /** Whether the current user is a GM */
    isGm?: boolean;
}

/**
 * Displays characters on a hex.
 * If the user is the owner and has no primary character, shows creation prompt.
 * If the user has a Camp Hall with capacity, shows recruitment option.
 * Character cards link to the character detail page when clicked.
 */
export function CharactersList({ hexId, isOwner, hasClaimedHex, isGm = false }: CharactersListProps) {
    const [characters, setCharacters] = useState<Character[]>([]);
    const [hasPrimaryCharacter, setHasPrimaryCharacter] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRecruitModal, setShowRecruitModal] = useState(false);
    const [recruitmentInfo, setRecruitmentInfo] = useState<RecruitmentInfo | null>(null);

    // Fetch characters data
    const fetchCharacters = useCallback(async () => {
        try {
            const response = await fetch(`/hex/${hexId}/characters`);
            if (!response.ok) throw new Error('Failed to fetch characters');
            const data: CharactersIndexResponse = await response.json();
            setCharacters(data.characters);
            setHasPrimaryCharacter(data.hasPrimaryCharacter);
            setError(null);
        } catch (err) {
            setError('Failed to load characters');
            console.error('Failed to fetch characters:', err);
        } finally {
            setIsLoading(false);
        }
    }, [hexId]);

    // Fetch recruitment info
    const fetchRecruitmentInfo = useCallback(async () => {
        if (!isOwner) return;
        try {
            const response = await fetch('/recruitment-info');
            if (!response.ok) throw new Error('Failed to fetch recruitment info');
            const data: RecruitmentInfo = await response.json();
            setRecruitmentInfo(data);
        } catch (err) {
            console.error('Failed to fetch recruitment info:', err);
        }
    }, [isOwner]);

    // Initial load
    useEffect(() => {
        fetchCharacters();
        fetchRecruitmentInfo();
    }, [fetchCharacters, fetchRecruitmentInfo]);

    // Refresh recruitment info after character creation
    const handleCharacterCreated = useCallback(() => {
        fetchCharacters();
        fetchRecruitmentInfo();
    }, [fetchCharacters, fetchRecruitmentInfo]);

    if (isLoading) {
        return (
            <div className="text-center text-gray-400 py-8">
                Loading characters...
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-red-400 py-8">
                {error}
            </div>
        );
    }

    // Show creation prompt if owner has no primary character and has claimed a hex
    const showCreationPrompt = isOwner && !hasPrimaryCharacter && hasClaimedHex;

    // Show recruitment option if owner has Camp Hall with capacity
    const showRecruitmentOption = isOwner && hasPrimaryCharacter && recruitmentInfo?.can_recruit;

    return (
        <div className="space-y-6">
            {/* Creation prompt for users without a primary character - parchment style */}
            {showCreationPrompt && (
                <div
                    className="relative p-6 rounded-sm text-center overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                        boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #c9b896',
                    }}
                >
                    {/* Decorative moth at top */}
                    <div className="flex justify-center mb-3">
                        <svg viewBox="0 0 100 80" className="w-14 h-12" style={{ opacity: 0.5 }}>
                            <ellipse cx="50" cy="45" rx="5" ry="14" fill="rgb(128, 90, 213)" />
                            <circle cx="50" cy="27" r="5" fill="rgb(128, 90, 213)" />
                            <path d="M46 24 Q42 16 38 14 M54 24 Q58 16 62 14" stroke="rgb(128, 90, 213)" strokeWidth="1.5" fill="none" />
                            <path d="M45 38 Q25 22 20 38 Q28 50 45 45" fill="rgb(128, 90, 213)" />
                            <path d="M55 38 Q75 22 80 38 Q72 50 55 45" fill="rgb(128, 90, 213)" />
                            <path d="M45 48 Q30 54 30 64 Q42 62 47 53" fill="rgba(128, 90, 213, 0.7)" />
                            <path d="M55 48 Q70 54 70 64 Q58 62 53 53" fill="rgba(128, 90, 213, 0.7)" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-amber-900 mb-2">
                        Create Your Celestial Moth
                    </h3>
                    <p className="text-base font-serif text-amber-800 mb-4">
                        Your Celestial Moth is your primary character in Sellune. Choose a class and begin your journey!
                    </p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-6 py-2 rounded font-serif font-semibold text-lg transition-all hover:scale-105"
                        style={{
                            background: 'linear-gradient(135deg, rgba(128, 90, 213, 0.9) 0%, rgba(107, 70, 193, 1) 100%)',
                            color: '#fff',
                            border: '1px solid rgba(128, 90, 213, 0.6)',
                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                        }}
                    >
                        Create Character
                    </button>
                </div>
            )}

            {/* Recruitment capacity info and button - parchment notice style */}
            {isOwner && hasPrimaryCharacter && recruitmentInfo && (
                <div
                    className="relative p-4 rounded-sm overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                        boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #c9b896',
                    }}
                >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">👥</span>
                            <div>
                                <div className="text-lg font-serif font-bold text-amber-900">
                                    Characters: {recruitmentInfo.current_count} / {recruitmentInfo.capacity.total}
                                </div>
                                {recruitmentInfo.capacity.has_camp_hall ? (
                                    <div className="text-sm font-serif text-amber-800/80">
                                        Camp Hall L{recruitmentInfo.capacity.camp_hall_level} {'★'.repeat(recruitmentInfo.capacity.camp_hall_stars)}{'☆'.repeat(5 - recruitmentInfo.capacity.camp_hall_stars)}
                                        {recruitmentInfo.xp_bonus > 0 && (
                                            <span className="text-emerald-700 font-semibold ml-2">+{recruitmentInfo.xp_bonus} XP bonus</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-sm font-serif text-amber-800/70">
                                        Build a Camp Hall to recruit more
                                    </div>
                                )}
                            </div>
                        </div>
                        {showRecruitmentOption ? (
                            <button
                                onClick={() => setShowRecruitModal(true)}
                                className="px-4 py-2 rounded font-serif font-semibold transition-all hover:scale-105 flex items-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(34, 139, 34, 0.9) 0%, rgba(28, 120, 28, 1) 100%)',
                                    color: '#fff',
                                    border: '1px solid rgba(34, 139, 34, 0.6)',
                                    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                                }}
                            >
                                <span>+</span>
                                <span>Recruit Character</span>
                            </button>
                        ) : recruitmentInfo.capacity.has_camp_hall && !recruitmentInfo.can_recruit ? (
                            <div className="text-base font-serif text-amber-800/70 italic">
                                {recruitmentInfo.reason || 'Recruitment capacity full'}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Characters list - 2 column grid of character sheets */}
            {characters.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {characters.map((character) => (
                        <CharacterSheet
                            key={character.id}
                            character={character}
                            linkToDetail={true}
                            isGm={isGm}
                            onCharacterUpdated={fetchCharacters}
                        />
                    ))}
                </div>
            ) : !showCreationPrompt ? (
                <div
                    className="text-center py-8 rounded-sm"
                    style={{
                        background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                        boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                        border: '1px solid #c9b896',
                    }}
                >
                    <span className="text-lg font-serif text-amber-800/70 italic">
                        No characters assigned to this hex.
                    </span>
                </div>
            ) : null}

            {/* Create character modal (for primary Celestial Moth) */}
            <CreateCharacterModal
                hexId={hexId}
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={handleCharacterCreated}
            />

            {/* Recruit character modal (for additional characters) */}
            <RecruitCharacterModal
                hexId={hexId}
                isOpen={showRecruitModal}
                recruitmentInfo={recruitmentInfo}
                onClose={() => setShowRecruitModal(false)}
                onCreated={handleCharacterCreated}
            />
        </div>
    );
}

export default CharactersList;

